import React from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ShieldAlert,
  Banknote,
  ShieldCheck,
  UserCheck,
  CheckSquare,
  Users,
  HeartHandshake as Handshake,
  MapPin,
  Info,
  ShieldBan,
} from "lucide-react";

const PrivacyPolicyPage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 },
    },
  };

  const sections = [
    {
      title: "Fraud & Abuse Policy",
      icon: ShieldBan,
      content: [
        "Any attempt to scam, misrepresent, or bypass the platform for abuse or harassment will result in immediate account suspension.",
        "All reported fraud will be reviewed and may result in legal action.",
      ],
      color: "text-red-600",
    },
    {
      title: "Payment Disclaimer",
      icon: Banknote,
      content: [
        "PartsLokator does not process payments between buyers and sellers. We are not liable for payment disputes.",
        "Always verify the buyer or seller before completing transactions.",
      ],
      color: "text-yellow-600",
    },
    {
      title: "Warranty Disclaimer",
      icon: ShieldAlert,
      content: [
        "Sellers define their own warranty terms. Buyers are responsible for confirming warranty details before purchasing.",
        "PartsLokator does not guarantee the condition or performance of any listed parts.",
      ],
      color: "text-orange-600",
    },
    {
      title: "Platform Code of Conduct",
      icon: Handshake,
      content: [
        "Be respectful: No abusive language or threats.",
        "Be accurate: Sellers must only list parts that exist and are available.",
        "Be timely: If you arrange a deal, show up or respond in a timely manner.",
      ],
      color: "text-blue-600",
    },
    {
      title: "Recommendations for Safe Transactions",
      icon: ShieldCheck,
      content: [
        "Meet in public for local pickups.",
        "Never send full payment until you’ve confirmed part details and terms.",
        "Take screenshots of your chat agreement if needed for reference.",
        "Use platforms like PayPal for protection when shipping.",
      ],
      color: "text-green-600",
    },
    {
      title: "Add-On Options to Protect Your Business",
      icon: UserCheck,
      content: [
        "Report User button on every profile or chat.",
        "Admin review on new listings or user behavior.",
        "Verified Seller badge for trusted businesses.",
        "Rating & reviews system to surface reliable sellers.",
        "Seller terms checkbox at part listing: 'I confirm this part is in my possession and accurately described.'",
      ],
      color: "text-purple-600",
    },
  ];

  return (
    <motion.div
      className="w-full"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.h1
        className="text-3xl md:text-4xl font-bold mb-8 text-center bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text"
        variants={itemVariants}
      >
        Platform Policies & Safety Guidelines
      </motion.h1>

      <motion.p
        className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto"
        variants={itemVariants}
      >
        Please read these guidelines carefully to ensure a safe and positive
        experience for everyone on PartsLokator.
      </motion.p>

      <div className="space-y-6">
        {sections.map((section, index) => (
          <motion.div key={index} variants={itemVariants}>
            <Card className="border shadow-sm bg-card/80 backdrop-blur-sm overflow-hidden">
              <CardHeader className="flex flex-row items-center gap-3 bg-muted/50 p-4 border-b">
                <section.icon
                  className={`h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0  ${
                    section.color || "text-primary"
                  }`}
                />
                <CardTitle className="text-lg sm:text-xl !m-0">
                  {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 text-muted-foreground">
                <ul className="space-y-3">
                  {section.content.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Info className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default PrivacyPolicyPage;
